import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  PolicyMinimalModel,
  PolicyModel,
  PolicyToAdd,
  PolicyToUpdateModel,
} from 'src/app/models/api/governance/policy.model';
import { HttpService } from '../../http/http.service';
import { FileDownloadModel } from 'src/app/models/api/fileDownload.model';
import { BulkDomainImportResponse } from 'src/app/models/api/governance/bulk-domain-import.model';
import { BulkEndpointImportResponse } from 'src/app/models/api/governance/bulk-endpoint-import.model';
import { SensitiveLabelsResponse } from 'src/app/models/api/governance/sensitive-label.model';
import { environment } from 'src/environments/environment';

export enum RuleType {
  risk = 'risk', // violation rules tab
  hygiene = 'hygiene', // hygiene rules tab
}

@Injectable({
  providedIn: 'root',
})
export class PoliciesHttpService {
  private readonly _policiesBaseUrl: string = 'governance/policies';
  private readonly _baseUrl = `${environment.api_base_url}/${environment.api_version}`;

  constructor(
    private readonly _httpService: HttpService,
    private readonly _http: HttpClient
  ) {}

  /**
   * @public Get all the existing policies on the account.
   * @returns an Observable{PolicyMinimalModel[]} with the policies returned from server. Returns a model policy object with fewer details than the complete model.
   */
  public getPolicies(): Observable<PolicyMinimalModel[]> {
    const url = `${this._policiesBaseUrl}`;
    return this._httpService.get<PolicyMinimalModel[]>(url);
  }

  public getIntegrationPolicies(integrationId: string): Observable<PolicyModel[]> {
    const url = `${this._policiesBaseUrl}/integration/${integrationId}`;
    return this._httpService.get<PolicyModel[]>(url);
  }

  public getPolicy(integrationId: string, policyId: string): Observable<PolicyModel> {
    const url = `${this._policiesBaseUrl}/integration/${integrationId}/policy/${policyId}`;
    return this._httpService.get<PolicyModel>(url);
  }

  public updatePolicy(
    integrationId: string,
    policyId: string,
    detailsToUpdate: PolicyToUpdateModel
  ): Observable<string> {
    const url = `${this._policiesBaseUrl}/integration/${integrationId}/policy/${policyId}`;
    return this._httpService.put<string, PolicyToUpdateModel>(url, detailsToUpdate);
  }

  public addPolicy(integrationId: string, policyToAdd: PolicyToAdd): Observable<string> {
    const url = `${this._policiesBaseUrl}/integration/${integrationId}/policy`;
    return this._httpService.post<PolicyToAdd, string>(url, policyToAdd);
  }

  public deleteCustomPolicy(integrationId: string, policyId: string): Observable<string> {
    const url = `${this._policiesBaseUrl}/integration/${integrationId}/policy/${policyId}`;
    return this._httpService.delete<string>(url);
  }

  public exportPolicyRules(integrationId: string, policyId: string, ruleType: RuleType): Observable<FileDownloadModel> {
    const url = `${this._policiesBaseUrl}/integration/${integrationId}/policy/${policyId}/rules/export?ruleType=${ruleType}`;
    return this._httpService.get<FileDownloadModel>(url);
  }

  public bulkImportDomains(integrationId: string, policyId: string, file: File): Observable<BulkDomainImportResponse> {
    const url = `${this._baseUrl}/${this._policiesBaseUrl}/integration/${integrationId}/policy/${policyId}/domains/bulk-import`;
    const formData = new FormData();
    formData.append('file', file);

    // Use HttpClient directly to avoid HttpService's default Content-Type: application/json header
    // Angular HttpClient will automatically set Content-Type: multipart/form-data with boundary for FormData
    return this._http.post<BulkDomainImportResponse>(url, formData);
  }

  public bulkImportEndpoints(
    integrationId: string,
    policyId: string,
    file: File
  ): Observable<BulkEndpointImportResponse> {
    const url = `${this._baseUrl}/${this._policiesBaseUrl}/integration/${integrationId}/policy/${policyId}/endpoints/bulk-import`;
    const formData = new FormData();
    formData.append('file', file);

    return this._http.post<BulkEndpointImportResponse>(url, formData);
  }

  public getSensitiveLabels(integrationId: string): Observable<SensitiveLabelsResponse> {
    const url = `${this._policiesBaseUrl}/integration/${integrationId}/sensitive-labels`;
    return this._httpService.get<SensitiveLabelsResponse>(url);
  }
}
